/**
 * Admin → Address Book (staff) tab — feature E05-F03 (E05-F03-T03).
 *
 * 1:1 port of the legacy `dashboard.js` staff UI (renderStaffList /
 * openStaffDialog / saveStaffContact / deleteStaffContact / showStaffQuickPick) +
 * `index.html#staff-dialog` (lines 766-772). It is the admin Settings tab that
 * LISTS the staff address book and offers full CRUD over `staff_contacts`.
 *
 * Parity, row-by-row (legacy renderStaffList):
 *   - a table with columns: name, email, a category BADGE, and the row actions
 *     (Edit / Delete);
 *   - the badge label + colour come from `staffModel.ts` (categoryLabel /
 *     categoryBadgeClass) which encode the legacy catColors map.
 *
 * Add / Edit dialog (legacy openStaffDialog + #staff-dialog):
 *   - fields: name (text), email (text), category (<select> with the five legacy
 *     options Sourcer / Recruiter / Manager / Notification Recipient / Other);
 *   - on ADD the select defaults to 'sourcer' (legacy openStaffDialog(null) →
 *     STAFF_ADD_DEFAULT_CATEGORY); on EDIT it is seeded from the contact;
 *   - Save → create (POST) for a new contact or update (PATCH) for an existing one
 *     (only the supplied fields, partial — staffApi).
 *
 * Delete (legacy deleteStaffContact): HARD delete via DELETE /admin/staff/:id; on
 * success the list invalidates and the row disappears.
 *
 * Transport: useStaff / useCreateContact / useUpdateContact / useDeleteContact
 * (staffApi.ts) → the shared apiClient. Pure presentation helpers (badge, options,
 * quick-pick filter) live in staffModel.ts and are unit-tested there.
 *
 * Also exports {@link StaffQuickPick} — the quick-pick recipient strip rendered
 * inside the Send/Notification dialog (legacy showStaffQuickPick): active-only,
 * filtered by a category, hidden when the filtered set is empty (T03 acceptance).
 */
import { useMemo, useState } from 'react';
import type { StaffContactDTO, StaffContactCategory } from '@eer/shared';
import { Dialog, DialogFooter, useToast } from '../ui/index.js';
import {
  STAFF_CATEGORY_OPTIONS,
  STAFF_ADD_DEFAULT_CATEGORY,
  categoryLabel,
  categoryBadgeClass,
  filterQuickPick,
} from './staffModel.js';
import { errorMessage } from '../api/client.js';
import { useStaff, useCreateContact, useUpdateContact, useDeleteContact } from './staffApi.js';

const FIELD_CLASS =
  'w-full rounded border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none';

// ---------------------------------------------------------------------------
// Category badge (renderStaffList)
// ---------------------------------------------------------------------------

/** The pill badge shown in the category column of a staff row. */
export function CategoryBadge({ category }: { readonly category: string }): React.JSX.Element {
  return (
    <span
      data-testid="staff-category-badge"
      data-category={category}
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${categoryBadgeClass(category)}`}
    >
      {categoryLabel(category)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Add / Edit dialog (#staff-dialog)
// ---------------------------------------------------------------------------

/** Mutable dialog-form state (mirrors the legacy #staff-dialog fields). */
export interface StaffForm {
  name: string;
  email: string;
  category: StaffContactCategory;
}

/**
 * Seed the dialog form. For a NEW contact (`contact == null`) the category is the
 * legacy Add default ('sourcer'); for an existing one it is seeded from the row.
 */
export function formFromContact(contact: StaffContactDTO | null): StaffForm {
  if (contact == null) {
    return { name: '', email: '', category: STAFF_ADD_DEFAULT_CATEGORY };
  }
  return {
    name: contact.name,
    email: contact.email,
    category: contact.category as StaffContactCategory,
  };
}

interface StaffDialogProps {
  /** The contact being edited, or null for an Add. */
  readonly contact: StaffContactDTO | null;
  readonly onClose: () => void;
}

function StaffDialog({ contact, onClose }: StaffDialogProps): React.JSX.Element {
  const toast = useToast();
  const createContact = useCreateContact();
  const updateContact = useUpdateContact();
  const [form, setForm] = useState<StaffForm>(() => formFromContact(contact));

  const isEdit = contact != null;
  const pending = createContact.isPending || updateContact.isPending;

  function patch<K extends keyof StaffForm>(key: K, value: StaffForm[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave(): void {
    const name = form.name.trim();
    const email = form.email.trim();
    // Mirror the legacy "Name and email are required" guard before the request.
    if (!name || !email) {
      toast.error('Name and email are required');
      return;
    }

    if (isEdit && contact) {
      updateContact.mutate(
        { id: contact.id, patch: { name, email, category: form.category } },
        {
          onSuccess: () => {
            toast.success('Contact saved');
            onClose();
          },
          onError: (error) => toast.error(errorMessage(error, 'Save error')),
        },
      );
    } else {
      createContact.mutate(
        { name, email, category: form.category },
        {
          onSuccess: () => {
            toast.success('Contact saved');
            onClose();
          },
          onError: (error) => toast.error(errorMessage(error, 'Save error')),
        },
      );
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? 'Edit contact' : 'Add contact'}
      maxWidth="md"
      testId="staff-dialog"
      footer={
        <DialogFooter
          onCancel={onClose}
          onConfirm={handleSave}
          confirmLabel="Save"
          confirmDisabled={pending}
          testId="staff-dialog-footer"
        />
      }
    >
      <div className="grid gap-4">
        <label htmlFor="staff-field-name" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Name</span>
          <input
            id="staff-field-name"
            data-testid="staff-field-name"
            type="text"
            className={FIELD_CLASS}
            value={form.name}
            onChange={(e) => patch('name', e.target.value)}
          />
        </label>

        <label htmlFor="staff-field-email" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Email</span>
          <input
            id="staff-field-email"
            data-testid="staff-field-email"
            type="email"
            className={FIELD_CLASS}
            value={form.email}
            onChange={(e) => patch('email', e.target.value)}
          />
        </label>

        <label htmlFor="staff-field-category" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Category</span>
          <select
            id="staff-field-category"
            data-testid="staff-field-category"
            className={FIELD_CLASS}
            value={form.category}
            onChange={(e) => patch('category', e.target.value as StaffContactCategory)}
          >
            {STAFF_CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Quick-pick strip (showStaffQuickPick) — used by the Send/Notification dialog
// ---------------------------------------------------------------------------

interface StaffQuickPickProps {
  /** The full staff list (typically from useStaff()). */
  readonly contacts: readonly StaffContactDTO[];
  /** Restrict the chips to this category (e.g. 'sourcer' for the Sourcing Sheet). */
  readonly filterCategory?: string;
  /** Called with the picked contact's email when a chip is clicked. */
  readonly onPick: (email: string) => void;
}

/**
 * Recipient quick-pick chips (legacy showStaffQuickPick). Renders ACTIVE contacts
 * matching `filterCategory`; renders NOTHING when the filtered set is empty (the
 * legacy code hid the whole strip in that case — T03 acceptance).
 */
export function StaffQuickPick({
  contacts,
  filterCategory,
  onPick,
}: StaffQuickPickProps): React.JSX.Element | null {
  const picks = useMemo(
    () => filterQuickPick(contacts, filterCategory),
    [contacts, filterCategory],
  );
  if (picks.length === 0) return null; // hidden when empty (parity)

  return (
    <div data-testid="staff-quick-pick" className="flex flex-wrap gap-1.5">
      {picks.map((c) => (
        <button
          key={c.id}
          type="button"
          data-testid="staff-quick-pick-chip"
          data-email={c.email}
          onClick={() => onPick(c.email)}
          className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700 transition hover:bg-gray-200"
          title={c.email}
        >
          {c.name}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staff row + tab
// ---------------------------------------------------------------------------

interface StaffRowProps {
  readonly contact: StaffContactDTO;
  readonly onEdit: (contact: StaffContactDTO) => void;
  readonly onDelete: (contact: StaffContactDTO) => void;
}

function StaffRow({ contact, onEdit, onDelete }: StaffRowProps): React.JSX.Element {
  return (
    <tr data-testid="staff-row" data-contact-id={contact.id} className="border-t border-border">
      <td data-testid="staff-row-name" className="px-3 py-2 text-sm text-text-strong">
        {contact.name}
      </td>
      <td data-testid="staff-row-email" className="px-3 py-2 text-sm text-gray-600">
        {contact.email}
      </td>
      <td className="px-3 py-2">
        <CategoryBadge category={contact.category} />
      </td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          data-testid="staff-row-edit"
          onClick={() => onEdit(contact)}
          className="mr-3 text-sm text-gray-400 hover:text-primary"
          aria-label="Edit contact"
          title="Edit contact"
        >
          <i className="fa-solid fa-pen" aria-hidden />
        </button>
        <button
          type="button"
          data-testid="staff-row-delete"
          onClick={() => onDelete(contact)}
          className="text-sm text-gray-400 hover:text-danger"
          aria-label="Delete contact"
          title="Delete contact"
        >
          <i className="fa-solid fa-trash" aria-hidden />
        </button>
      </td>
    </tr>
  );
}

/**
 * The Address Book admin tab. Lists active contacts (server orders category ASC,
 * name ASC) and offers Add / Edit / Delete. HARD delete (legacy parity).
 */
export function AddressBookTab(): React.JSX.Element {
  const query = useStaff();
  const deleteContact = useDeleteContact();
  const toast = useToast();
  const [editing, setEditing] = useState<StaffContactDTO | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const contacts = useMemo(() => query.data ?? [], [query.data]);

  function openAdd(): void {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(contact: StaffContactDTO): void {
    setEditing(contact);
    setDialogOpen(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
    setEditing(null);
  }

  function handleDelete(contact: StaffContactDTO): void {
    deleteContact.mutate(contact.id, {
      onSuccess: () => toast.success('Contact deleted'),
      onError: (error) => toast.error(errorMessage(error, 'Delete error')),
    });
  }

  if (query.isLoading) {
    return (
      <div data-testid="staff-loading" className="py-4 text-center text-gray-400">
        Loading…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div data-testid="staff-error" className="py-4 text-center text-red-500">
        {errorMessage(query.error, 'Failed to load staff')}
      </div>
    );
  }

  return (
    <div data-testid="staff-tab">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-medium text-text-strong">Address Book</h2>
        <button
          type="button"
          data-testid="staff-add"
          onClick={openAdd}
          className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
        >
          <i className="fa-solid fa-plus mr-1" aria-hidden /> Add
        </button>
      </div>
      <p className="mb-4 text-xs text-gray-500">
        Quick-pick contacts for sending emails (sourcers, recruiters, managers)
      </p>

      {contacts.length === 0 ? (
        <div data-testid="staff-empty" className="py-4 text-center text-gray-400">
          No contacts
        </div>
      ) : (
        <table data-testid="staff-table" className="w-full border-collapse">
          <thead>
            <tr className="text-left text-xs font-medium tracking-wide text-gray-500">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <StaffRow key={c.id} contact={c} onEdit={openEdit} onDelete={handleDelete} />
            ))}
          </tbody>
        </table>
      )}

      {dialogOpen ? <StaffDialog contact={editing} onClose={closeDialog} /> : null}
    </div>
  );
}

export default AddressBookTab;
